import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DiscoverCardPreview from './DiscoverCardPreview';

const POSITION_PRESETS = [
  { id: 'north', label: 'North', focusY: 0 },
  { id: 'attention', label: 'Attention', focusY: 0.25 },
  { id: 'centre', label: 'Centre', focusY: 0.5 },
  { id: 'south', label: 'South', focusY: 1 },
];

function nearestPresetId(focusY) {
  let best = null;
  let bestDist = Infinity;
  for (const p of POSITION_PRESETS) {
    const d = Math.abs(p.focusY - focusY);
    if (d < bestDist && d < 0.06) {
      best = p.id;
      bestDist = d;
    }
  }
  return best;
}

/**
 * Adjust crop focus for 5:2 Pulse cover banner.
 * Applies via POST …/cover-crop — clean photo crop only (no title burned onto the image).
 * Live preview uses CSS object-position only.
 */
function CoverCropPanel({
  sourceImageUrl,
  scrapeHeroUrl,
  coverTitle,
  summary,
  disabled,
  applying,
  onApply,
  onSourceImageUrlChange,
}) {
  const [focusX, setFocusX] = useState(0.5);
  const [focusY, setFocusY] = useState(0.5);
  const [titleOverride, setTitleOverride] = useState('');
  const activePreset = useMemo(() => nearestPresetId(focusY), [focusY]);

  const canApply = !!sourceImageUrl?.trim() && !disabled && !applying;
  const usingScrapeHero =
    !!scrapeHeroUrl && !!sourceImageUrl && sourceImageUrl.trim() === scrapeHeroUrl.trim();

  return (
    <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
      <Typography variant="subtitle2" gutterBottom>
        Adjust Pulse cover crop
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        5:2 cover photo. Prefer the scrape hero (uncropped) — re-cropping an already-cropped cover
        barely changes the frame. Title is saved as Pulse card text only; it is{' '}
        <strong>not</strong> drawn onto the image. Default crop is centre (balanced left/right).
      </Typography>

      <TextField
        fullWidth
        size="small"
        label="Source image URL (for crop)"
        value={sourceImageUrl || ''}
        onChange={(e) => onSourceImageUrlChange?.(e.target.value)}
        disabled={disabled}
        sx={{ mb: 1 }}
        helperText={
          usingScrapeHero
            ? 'Using scrape hero (uncropped)'
            : 'Prefer scrape hero — avoid re-cropping a finished cover'
        }
      />

      {scrapeHeroUrl && !usingScrapeHero && (
        <Button
          size="small"
          variant="outlined"
          sx={{ mb: 1.5 }}
          disabled={disabled}
          onClick={() => onSourceImageUrlChange?.(scrapeHeroUrl)}
        >
          Use scrape hero
        </Button>
      )}

      <Box sx={{ mb: 1.5, maxWidth: 360 }}>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          Live crop preview (source + focus)
        </Typography>
        <DiscoverCardPreview
          coverTitle={titleOverride.trim() || coverTitle}
          summary={summary}
          coverImageUrl={sourceImageUrl}
          slideCount={0}
          coverAspectRatio="5:2"
          focusX={focusX}
          focusY={focusY}
          showTitleOverlay={false}
          showSlideCountBadge={false}
          imageKey={`crop-src-${sourceImageUrl}-${focusX}-${focusY}`}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          focusX {focusX.toFixed(2)} · focusY {focusY.toFixed(2)}
        </Typography>
      </Box>

      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5, gap: 1 }}>
        {POSITION_PRESETS.map((p) => (
          <Chip
            key={p.id}
            label={p.label}
            size="small"
            color={activePreset === p.id ? 'primary' : 'default'}
            variant={activePreset === p.id ? 'filled' : 'outlined'}
            onClick={() => {
              if (disabled) return;
              setFocusY(p.focusY);
              setFocusX(0.5);
            }}
            disabled={disabled}
          />
        ))}
      </Stack>

      <Typography variant="caption" color="text.secondary" display="block">
        Focus X (left → right)
      </Typography>
      <Slider
        value={focusX}
        min={0}
        max={1}
        step={0.01}
        onChange={(_, v) => setFocusX(v)}
        disabled={disabled}
        valueLabelDisplay="auto"
        sx={{ mb: 1, maxWidth: 360 }}
      />

      <Typography variant="caption" color="text.secondary" display="block">
        Focus Y (top → bottom)
      </Typography>
      <Slider
        value={focusY}
        min={0}
        max={1}
        step={0.01}
        onChange={(_, v) => setFocusY(v)}
        disabled={disabled}
        valueLabelDisplay="auto"
        sx={{ mb: 1.5, maxWidth: 360 }}
      />

      <TextField
        fullWidth
        size="small"
        label="Pulse title override (optional)"
        value={titleOverride}
        onChange={(e) => setTitleOverride(e.target.value)}
        disabled={disabled}
        helperText="Updates cover_title text for Pulse — not burned onto the crop. Empty keeps saved title."
        sx={{ mb: 1.5 }}
      />

      <Button
        variant="contained"
        size="small"
        disabled={!canApply}
        onClick={() => {
          const url = (sourceImageUrl || '').trim();
          if (!url) return;
          onApply?.({
            sourceImageUrl: url,
            focusX: Number(focusX),
            focusY: Number(focusY),
            coverTitle: titleOverride.trim() || coverTitle?.trim() || undefined,
          });
        }}
        startIcon={applying ? <CircularProgress size={14} color="inherit" /> : null}
      >
        {applying ? 'Applying…' : 'Apply clean crop'}
      </Button>
    </Box>
  );
}

export default CoverCropPanel;
